import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X,
  Users,
  Loader2,
  Search,
  Building2,
  Check,
  Globe,
} from 'lucide-react';
import { projectsApi, organizationsApi } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { cn } from '@/lib/utils';
import { useAuthStore, getIsAgencyAdmin } from '@/stores/auth.store';

interface ManageMembersModalProps {
  isOpen: boolean;
  projectId: string;
  project: any;
  onClose: () => void;
  readOnly?: boolean;
}

interface OrganizationMember {
  userId: string;
  user: {
    id: string;
    name: string;
    email: string;
    avatarUrl?: string;
  };
  isMember?: boolean;
}

export function ManageMembersModal({ isOpen, projectId, project, onClose, readOnly = false }: ManageMembersModalProps) {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Get current project members
  const currentMemberIds = new Set(project?.members?.map((m: any) => m.userId) || []);
  const currentMemberEmails = new Set(
    project?.members?.map((m: any) => m.user?.email).filter(Boolean) || []
  );

  // The agency that staffs this project from the caller's side: Solid, or — on
  // a partner agency's project — that agency.
  const userAgencyOrg =
    (user as any)?.organizations?.find(
      (org: any) => org.type === 'AGENCY' || org.organization?.type === 'AGENCY',
    ) ??
    (user as any)?.organizations?.find(
      (org: any) => org.type === 'PARTNER' && org.id === project?.organizationId,
    );
  const agencyOrgId = userAgencyOrg?.id || userAgencyOrg?.organization?.id;
  const agencyOrgName = userAgencyOrg?.name || userAgencyOrg?.organization?.name || 'Agency';

  // Searching every Hub user stays Solid-only (so does its API route)
  const canSearchAllUsers = getIsAgencyAdmin(user);

  // For imported projects, project.organizationId is the client org
  const projectOrgId = project?.organizationId;
  const explicitClientOrgId = project?.settings?.clientOrganizationId;
  const clientOrgId = (explicitClientOrgId && (explicitClientOrgId !== agencyOrgId)) ? explicitClientOrgId : (projectOrgId !== agencyOrgId ? projectOrgId : null);

  // Tab state: 'members' (default), 'agency', 'client', 'search'
  const [selectedTab, setSelectedTab] = useState<'members' | 'agency' | 'client' | 'search'>('members');

  // Reset tab and search when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedTab('members');
      setSearchQuery('');
      setDebouncedSearch('');
    }
  }, [isOpen]);

  // Debounce search query for backend search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Switch to search tab when typing (if agency admin and a separate client org exists).
  // For agency-owned projects (no clientOrgId) there is no cross-org context, so typing
  // just filters the Agency tab inline — no separate Search Results tab needed.
  useEffect(() => {
    if (searchQuery.length >= 2 && canSearchAllUsers && !readOnly && clientOrgId) {
      setSelectedTab('search');
    } else if (searchQuery.length === 0 && selectedTab === 'search') {
      setSelectedTab('members');
    }
  }, [searchQuery, canSearchAllUsers, readOnly, clientOrgId]);

  // Get agency organization members
  const { data: agencyMembers, isLoading: agencyLoading } = useQuery({
    queryKey: ['organization-members', agencyOrgId],
    queryFn: () => organizationsApi.getMembers(agencyOrgId),
    enabled: isOpen && !!agencyOrgId && selectedTab === 'agency',
  });

  // Get client organization members (if different from agency)
  const { data: clientMembers, isLoading: clientLoading } = useQuery({
    queryKey: ['organization-members', clientOrgId],
    queryFn: () => organizationsApi.getMembers(clientOrgId!),
    enabled: isOpen && !!clientOrgId && selectedTab === 'client',
  });

  // Get client org name
  const { data: clientOrg } = useQuery({
    queryKey: ['organization', clientOrgId],
    queryFn: () => organizationsApi.get(clientOrgId!),
    enabled: isOpen && !!clientOrgId && !readOnly,
  });
  const clientOrgName = clientOrg?.name
    || (clientOrgId === projectOrgId ? project?.organization?.name : null)
    || (user as any)?.organizations?.find((o: any) => o.id === clientOrgId)?.name
    || 'Client';

  // Cross-org user search (agency admin only)
  const { data: searchResults, isLoading: searchLoading } = useQuery({
    queryKey: ['project-search-users', projectId, debouncedSearch],
    queryFn: () => projectsApi.searchUsers(projectId, debouncedSearch),
    enabled: isOpen && !!debouncedSearch && debouncedSearch.length >= 2 && canSearchAllUsers && !readOnly,
  });

  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: ({ userId, email, name, avatarUrl }: { userId: string; email?: string; name?: string; avatarUrl?: string }) =>
      projectsApi.addMember(projectId, userId, { email, name, avatarUrl }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-search-users', projectId] });
    },
    onError: (error) => {
      console.error('Failed to add member:', error);
    },
  });

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => projectsApi.removeMember(projectId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-search-users', projectId] });
    },
    onError: (error) => {
      console.error('Failed to remove member:', error);
    },
  });

  const handleToggleMember = (e: React.MouseEvent, member: OrganizationMember) => {
    e.preventDefault();
    e.stopPropagation();
    const { userId, user } = member;
    const isAlreadyMember = currentMemberIds.has(userId) || currentMemberEmails.has(user.email);
    if (isAlreadyMember) {
      // Use the canonical (DB/JWT-sourced) userId, which may differ from the accounts API userId
      const canonicalMember = project?.members?.find(
        (m: any) => m.userId === userId || m.user?.email === user.email
      );
      removeMemberMutation.mutate(canonicalMember?.userId || userId);
    } else {
      addMemberMutation.mutate({ userId, email: user.email, name: user.name, avatarUrl: user.avatarUrl });
    }
  };

  // Filter and sort members - project members first, then alphabetically
  const filterAndSortMembers = (members: OrganizationMember[] = []) => {
    let filtered = members;
    // In read-only mode, only show actual project members
    if (readOnly) {
      filtered = filtered.filter(m => currentMemberIds.has(m.userId));
    }
    if (searchQuery && selectedTab !== 'search') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(m =>
        m.user.name?.toLowerCase().includes(query) ||
        m.user.email?.toLowerCase().includes(query)
      );
    }
    // Sort: project members first, then alphabetically by name
    return filtered.sort((a, b) => {
      const aIsMember = currentMemberIds.has(a.userId);
      const bIsMember = currentMemberIds.has(b.userId);
      if (aIsMember && !bIsMember) return -1;
      if (!aIsMember && bIsMember) return 1;
      return (a.user.name || '').localeCompare(b.user.name || '');
    });
  };

  // Build current project members list from project data
  const projectMembers: OrganizationMember[] = useMemo(() => {
    if (!project?.members) return [];
    return project.members.map((m: any) => ({
      userId: m.userId,
      user: {
        id: m.userId,
        name: m.user?.name || m.userName || 'Unknown',
        email: m.user?.email || m.userEmail || '',
        avatarUrl: m.user?.avatarUrl || m.userAvatarUrl,
      },
    }));
  }, [project?.members]);

  const filteredProjectMembers = useMemo(
    () => {
      let filtered = projectMembers;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(m =>
          m.user.name?.toLowerCase().includes(query) ||
          m.user.email?.toLowerCase().includes(query)
        );
      }
      return filtered.sort((a, b) => (a.user.name || '').localeCompare(b.user.name || ''));
    },
    [projectMembers, searchQuery]
  );

  const filteredAgencyMembers = useMemo(
    () => filterAndSortMembers(agencyMembers),
    [agencyMembers, searchQuery, currentMemberIds]
  );
  const filteredClientMembers = useMemo(
    () => filterAndSortMembers(clientMembers),
    [clientMembers, searchQuery, currentMemberIds]
  );

  // Search results mapped to OrganizationMember format
  const searchResultMembers: OrganizationMember[] = useMemo(() => {
    if (!searchResults) return [];
    return searchResults.map(r => ({
      userId: r.userId,
      user: r.user,
      isMember: r.isMember,
    }));
  }, [searchResults]);

  let currentMembers: OrganizationMember[];
  let isTabLoading: boolean;

  switch (selectedTab) {
    case 'members':
      currentMembers = filteredProjectMembers;
      isTabLoading = false;
      break;
    case 'agency':
      currentMembers = filteredAgencyMembers;
      isTabLoading = agencyLoading;
      break;
    case 'client':
      currentMembers = filteredClientMembers;
      isTabLoading = clientLoading;
      break;
    case 'search':
      currentMembers = searchResultMembers;
      isTabLoading = searchLoading;
      break;
    default:
      currentMembers = filteredProjectMembers;
      isTabLoading = false;
  }

  const isLoading = addMemberMutation.isPending || removeMemberMutation.isPending;

  if (!isOpen) return null;

  const renderMemberCard = (member: OrganizationMember) => {
    const isMember = member.isMember !== undefined
      ? member.isMember
      : currentMemberIds.has(member.userId) || currentMemberEmails.has(member.user.email);
    const isPending = isLoading;
    const canToggle = !readOnly && !isPending;

    return (
      <div
        key={member.userId}
        onClick={(e) => canToggle && handleToggleMember(e, member)}
        className={cn(
          "flex items-center gap-2 p-2 rounded-lg transition-all",
          canToggle && "cursor-pointer",
          !canToggle && "cursor-default",
          readOnly
            ? "bg-grey-50 border border-transparent"
            : isMember
              ? "bg-accent/10 border border-accent/20"
              : "bg-grey-50 border border-transparent",
          canToggle && isMember && "hover:bg-accent/15",
          canToggle && !isMember && "hover:bg-grey-100 hover:border-grey-200",
          isPending && "opacity-50 cursor-wait"
        )}
      >
        <div className="relative">
          <Avatar
            src={member.user.avatarUrl}
            name={member.user.name}
            userId={member.user.id}
            size="sm"
          />
          {!readOnly && isMember && (
            <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-accent rounded-full flex items-center justify-center">
              <Check className="w-2.5 h-2.5 text-white" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-secondary truncate">
            {member.user.name || member.user.email.split('@')[0]}
          </p>
          <p className="text-xs text-grey-500 truncate">{member.user.email}</p>
        </div>
        {isPending && (
          <Loader2 className="w-4 h-4 text-grey-400 animate-spin shrink-0" />
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-secondary/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal - wider for grid layout */}
      <div
        className="relative bg-white rounded-2xl shadow-elevated max-w-4xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-grey-100">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-accent" />
            <h2 className="text-heading-3 text-secondary">
              {readOnly ? 'Project Members' : 'Manage Project Members'}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="btn-icon">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-grey-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
              placeholder={canSearchAllUsers && !readOnly ? "Search all users by name or email..." : "Search by name or email..."}
              className="input pl-10"
            />
          </div>
          {canSearchAllUsers && !readOnly && searchQuery.length > 0 && searchQuery.length < 2 && (
            <p className="text-xs text-grey-400 mt-1 ml-1">Type at least 2 characters to search all users</p>
          )}
        </div>

        {/* Tabs */}
        {!readOnly && (
          <div className="flex border-b border-grey-100">
            <button
              type="button"
              onClick={() => { setSelectedTab('members'); setSearchQuery(''); }}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                selectedTab === 'members'
                  ? "border-accent text-accent"
                  : "border-transparent text-grey-500 hover:text-secondary"
              )}
            >
              <Users className="w-4 h-4" />
              Members
              <span className="text-xs bg-grey-100 text-grey-600 px-1.5 py-0.5 rounded-full">
                {currentMemberIds.size}
              </span>
            </button>
            {clientOrgId && (
              <button
                type="button"
                onClick={() => { setSelectedTab('client'); setSearchQuery(''); }}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                  selectedTab === 'client'
                    ? "border-accent text-accent"
                    : "border-transparent text-grey-500 hover:text-secondary"
                )}
              >
                <Building2 className="w-4 h-4" />
                {clientOrgName}
              </button>
            )}
            <button
              type="button"
              onClick={() => { setSelectedTab('agency'); setSearchQuery(''); }}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                selectedTab === 'agency'
                  ? "border-accent text-accent"
                  : "border-transparent text-grey-500 hover:text-secondary"
              )}
            >
              <Building2 className="w-4 h-4" />
              {agencyOrgName}
            </button>
            {canSearchAllUsers && selectedTab === 'search' && (
              <button
                type="button"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 border-accent text-accent"
              >
                <Globe className="w-4 h-4" />
                Search Results
              </button>
            )}
          </div>
        )}

        {/* Content - Grid layout */}
        <div className="flex-1 overflow-y-auto p-4">
          {isTabLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
            </div>
          ) : currentMembers.length === 0 ? (
            <div className="py-12 text-center text-grey-500">
              {selectedTab === 'search' && debouncedSearch.length >= 2
                ? 'No users found matching your search'
                : selectedTab === 'members'
                  ? 'No members on this project yet'
                  : searchQuery
                    ? 'No members match your search'
                    : 'No members available'}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {currentMembers.map(renderMemberCard)}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 bg-grey-50 border-t border-grey-100">
          <p className="text-sm text-grey-500">
            {currentMemberIds.size} {currentMemberIds.size === 1 ? 'member' : 'members'} on this project
            {selectedTab === 'search' && searchResults && (
              <span className="text-grey-400 ml-2">
                · {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
              </span>
            )}
          </p>
          <Button onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
